import useSourceFlowCollection from "@/hooks/useSourceFlowCollection";
import data from "@sourceflow/case_studies.json";
import tags from "@sourceflow/tags.json";

/**
 * Case Studies (SourceFlow CMS category: "Case Studies")
 *
 * Category schema:
 *  Name          text            company name (identifier)
 *  Logo          file            company logo
 *  Stats         object (repeatable)
 *                  Stats         text            headline figure, e.g. "7 weeks"
 *                  Description   text            the line printed under the figure
 *  Content       formatted_text  what we did
 *  Testimonials  object (repeatable)
 *                  Testimonial   formatted_text  the quote
 *                  Name          text
 *                  Role          text
 *  Tags          category (multi, "Tags" category)
 *
 * The repeatable fields are flattened into `statistics` and `testimonials` so
 * the components never deal with the CMS key names.
 */
const statistics = (item) =>
  (Array.isArray(item.stats) ? item.stats : [])
    .map(({ id, stats, description }) => ({
      id,
      value: stats ?? null,
      description: description ?? null,
    }))
    .filter(({ value, description }) => value || description);

const testimonials = (item) =>
  (Array.isArray(item.testimonials) ? item.testimonials : [])
    .map(({ id, testimonial, name, role }) => ({
      id,
      quote: testimonial ?? null,
      name: name ?? null,
      role: role ?? null,
    }))
    .filter(({ quote }) => quote);

// Case studies have no page of their own, so each one is addressed by an
// anchor on /case-studies/ built from its name.
const anchor = (item) =>
  String(item.name ?? item.id)
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const itemTags = (item) => (Array.isArray(item.tags) ? item.tags : []);

const matchesTag = (item, tag) =>
  itemTags(item).some(
    (t) => t?.id === tag || t?.name?.toLowerCase() === String(tag).toLowerCase().trim(),
  );

export const useCaseStudies = ({ tags: filterTags = null, names = null, limit = null } = {}) => {
  let items = useSourceFlowCollection(data, { tags }).map((i) => ({
    ...i,
    tags: itemTags(i),
    anchor: anchor(i),
    href: `/case-studies/#${anchor(i)}`,
    statistics: statistics(i),
    testimonials: testimonials(i),
  }));

  if (filterTags) {
    const wanted = Array.isArray(filterTags) ? filterTags : [filterTags];

    items = items.filter((i) => wanted.some((tag) => matchesTag(i, tag)));
  }

  if (names) {
    const wanted = (Array.isArray(names) ? names : [names]).map((n) =>
      String(n).toLowerCase().trim(),
    );

    items = items
      .filter((i) => wanted.includes(i.name?.toLowerCase()))
      .sort(
        (a, b) => wanted.indexOf(a.name?.toLowerCase()) - wanted.indexOf(b.name?.toLowerCase()),
      );
  }

  if (limit) {
    items = items.slice(0, limit);
  }

  return items;
};

export default useCaseStudies;
